import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, IconButton, Tooltip } from '@mui/material';
import { Logout as LogoutIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FinMark from '../brand/FinMark';
import { useAuth } from '../../contexts/AuthContext';
import { BOT_URL } from '../../config/telegram';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import ConnectTelegramDialog, { useConnectTelegramFor } from '../contexts/ConnectTelegramDialog';
import { useFinChannel } from '../../hooks/useFinChannel';
import { tokens } from '../../theme/tokens';
import { isActivePath, mainNav, NavItem, settingsNav } from './navItems';

export const SIDEBAR_WIDTH = 248;

const NavLink: React.FC<{ item: NavItem; active: boolean; label: string }> = ({ item, active, label }) => (
  <Box
    component={RouterLink}
    to={item.path}
    aria-current={active ? 'page' : undefined}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      minHeight: 44,
      px: 1.5,
      borderRadius: '12px',
      textDecoration: 'none',
      fontWeight: active ? 700 : 500,
      color: active ? tokens.accent : tokens.ink2,
      bgcolor: active ? tokens.accentSoft : 'transparent',
      '& svg': { fontSize: 21 },
      '&:hover': { bgcolor: active ? tokens.accentSoft : tokens.bg, color: active ? tokens.accent : tokens.ink },
    }}
  >
    {item.icon}
    {label}
  </Box>
);

/** The desktop sidebar: the places, a door to the Fin, settings and the account. */
const Sidebar: React.FC = () => {
  const { t } = useTranslation('navigation');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const channel = useFinChannel();
  const { selectedContext } = useFinancialContexts();
  const { openDialog, dialogProps } = useConnectTelegramFor(selectedContext);
  const user = state.user;
  const linked = Boolean(user?.isTelegramLinked);
  const inGroup = channel.kind !== 'personal';

  // A group is reached in its own Telegram group, never in the private chat
  // (that records into the personal view), so its card has no "open" button.
  const finText = (() => {
    if (!linked) return t('shell.finCardConnectText');
    switch (channel.kind) {
      case 'personal':
        return t('shell.finCardText');
      case 'groupChat': {
        const text = channel.chatTitle
          ? t('shell.finCardGroupChatText', { chat: channel.chatTitle })
          : t('shell.finCardGroupChatTextUntitled', { name: channel.groupName });
        return !channel.url && channel.needsAdmin ? `${text} ${t('shell.makeFinAdmin')}` : text;
      }
      default:
        return channel.canAddFin ? t('shell.finCardAddFinText') : t('shell.finCardAskAdminText');
    }
  })();
  const finButton = (() => {
    // Messages from a Telegram account that isn't linked land in another account: link first.
    if (!linked) {
      return (
        <Button component={RouterLink} to='/settings/telegram' variant='contained' size='small' startIcon={<TelegramIcon />} sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}>
          {t('shell.connectTelegram')}
        </Button>
      );
    }
    // Adding Fin opens the same window as on Grupos, which waits for Fin and offers the invite.
    if (channel.kind === 'noGroupChat' && channel.canAddFin) {
      return (
        <Button onClick={openDialog} variant='contained' size='small' startIcon={<TelegramIcon />} sx={{ alignSelf: 'flex-start' }}>
          {t('shell.addToGroup')}
        </Button>
      );
    }
    const target =
      channel.kind === 'personal'
        ? { href: BOT_URL, label: t('shell.openTelegram') }
        : channel.kind === 'groupChat' && channel.url
          ? { href: channel.url, label: t('shell.openGroupShort') }
          : null;
    if (!target) return null;
    return (
      <Button
        href={target.href}
        target='_blank'
        rel='noopener noreferrer'
        variant='contained'
        size='small'
        startIcon={<TelegramIcon />}
        sx={{ alignSelf: 'flex-start' }}
      >
        {target.label}
      </Button>
    );
  })();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <Box
      component='nav'
      aria-label={t('shell.mainMenu')}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        bgcolor: tokens.surface,
        px: 1.75,
        pt: 2.75,
        pb: 2.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box
        component={RouterLink}
        to='/dashboard'
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, pb: 2.75, color: tokens.ink, textDecoration: 'none' }}
      >
        <FinMark size={32} />
        <Box component='span' sx={{ fontFamily: tokens.display, fontWeight: 700, fontSize: 22, letterSpacing: '-0.02em' }}>
          Financy
        </Box>
      </Box>

      {mainNav.map((item) => (
        <NavLink key={item.key} item={item} active={isActivePath(pathname, item.path)} label={t(`menu.${item.key}`)} />
      ))}

      <Box sx={{ mt: 'auto', pt: 3.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ mx: 0.5, mb: 1.75, p: 2, borderRadius: '16px', bgcolor: tokens.accentSoft, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box component='strong' sx={{ fontSize: 14 }}>
            {inGroup ? t('shell.finCardGroupTitle') : t('shell.finCardTitle')}
          </Box>
          <Box component='span' sx={{ fontSize: 13, color: tokens.ink2, lineHeight: 1.45 }}>
            {finText}
          </Box>
          {finButton}
        </Box>

        <NavLink item={settingsNav} active={isActivePath(pathname, settingsNav.path)} label={t(`menu.${settingsNav.key}`)} />

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: 1.25, pl: 1.5, pr: 0.5, mt: 1, borderTop: `1px solid ${tokens.line}` }}>
            <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: tokens.accentSoft2, color: tokens.accent }}>
              {user.firstName?.charAt(0).toUpperCase() || '?'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Box component='strong' sx={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.fullName || user.firstName}
              </Box>
              <Box component='span' sx={{ fontSize: 12, color: tokens.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </Box>
            </Box>
            <Tooltip title={t('header.logout')}>
              <IconButton aria-label={t('header.logout')} onClick={handleLogout} sx={{ color: tokens.ink3 }}>
                <LogoutIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <ConnectTelegramDialog {...dialogProps} />
    </Box>
  );
};

export default Sidebar;
